// src/components/kyc/ApplicantEntityDirectorsStep.tsx
import React, { useEffect, useMemo, useState } from "react";
import GlobalTextInputField from "../../components/GlobalTextInputField";
import UploadTableSingle from "./UploadTableSingle";
import Info from "../../assets/info.svg";

import type { Director, Step3State } from "../../types/types";
import UploadTable from "./UploadTable";

type Props = {
    value: Step3State;
    onChange: (patch: Partial<Step3State>) => void;
    readOnly?: boolean;
    isAdmin?: boolean;
};

const Label: React.FC<React.PropsWithChildren> = ({ children }) => (
    <label className="text-[10px] font-semibold text-text-base">{children}</label>
);

const emptyDirector: Director = {
    fullName: "",
    positionTitle: "",
    dateOfBirth: "",
    nationality: "",
    passportNo: "",
    email: "",
    phone: "",
    address: "",
    isPep: "",
    addressProofUrl: "",
    passportCopyUrl: "",
    additionalUrls: [],
};

// 1 -> "1st", 2 -> "2nd", etc.
const ordinal = (n: number) => {
    const v = n % 100;
    if (v >= 11 && v <= 13) return `${n}th`;
    switch (n % 10) {
        case 1: return `${n}st`;
        case 2: return `${n}nd`;
        case 3: return `${n}rd`;
        default: return `${n}th`;
    }
};

const ApplicantEntityDirectorsStep: React.FC<Props> = ({
    value,
    onChange,
    readOnly,
    isAdmin
}) => {
    const directors = useMemo(() => {
        const src = value.directors?.length
            ? value.directors
            : [{ ...emptyDirector }];

        return src.map((d) => ({
            ...emptyDirector,
            ...d,
            additionalUrls: Array.isArray(d.additionalUrls) ? d.additionalUrls : [],
        }));
    }, [value.directors]);

    useEffect(() => {
        if (!value.directors?.length) {
            onChange({ directors });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [active, setActive] = useState(0);

    useEffect(() => {
        if (active > directors.length - 1) {
            setActive(Math.max(0, directors.length - 1));
        }
    }, [directors.length, active]);

    const setDirectors = (next: Director[]) => onChange({ directors: next });

    const updateDirector = (idx: number, patch: Partial<Director>) => {
        const next = directors.map((d, i) => (i === idx ? { ...d, ...patch } : d));
        setDirectors(next);
    };

    const addDirector = () => {
        setDirectors([...directors, { ...emptyDirector }]);
        setActive(directors.length);
    };

    const removeDirector = (idx: number) => {
        if (directors.length === 1) {
            setDirectors([{ ...emptyDirector }]);
            setActive(0);
            return;
        }
        const next = directors.filter((_, i) => i !== idx);
        setDirectors(next);
        if (active >= next.length) setActive(next.length - 1);
    };

    const d = directors[active];

    return (
        <div className="space-y-4">
            <p className="text-text-base text-[10px]">
                List all directors of the entity as registered with the relevant company
                registry. For each director, provide full personal details, including
                proof of identity and address. This information is required for due
                diligence and to confirm whether any directors are politically exposed
                persons (PEPs).
            </p>

            {/* Tabs header */}
            <div className="flex items-center gap-3 overflow-x-auto">
                {directors.map((_, i) => {
                    const isActive = i === active;
                    return (
                        <button
                            key={i}
                            type="button"
                            onClick={() => setActive(i)}
                            className={`rounded-md px-3 py-1.5 text-[14px] font-semibold whitespace-nowrap
                                ${isActive
                                    ? "bg-[#E6F0FA] text-text-base"
                                    : "bg-transparent text-text-base/80 hover:bg-black/5"
                                }`}
                            disabled={readOnly}
                        >
                            {ordinal(i + 1)}
                        </button>
                    );
                })}
                {!readOnly && (
                    <button
                        type="button"
                        onClick={addDirector}
                        className="text-[14px] font-semibold whitespace-nowrap hover:underline"
                    >
                        + Add a new Director
                    </button>
                )}
            </div>

            {/* Active director card */}
            <div className="rounded-lg border border-border-subtle p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-[14px] font-semibold">
                        Director {ordinal(active + 1)}
                    </h3>
                    {!readOnly && (
                        <button
                            type="button"
                            onClick={() => removeDirector(active)}
                            className="text-[12px] underline disabled:opacity-50"
                            disabled={directors.length === 1}
                        >
                            Remove
                        </button>
                    )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <GlobalTextInputField
                        title="FULL NAME"
                        value={d.fullName}
                        placeholder="Enter full name"
                        onChange={(e: any) =>
                            updateDirector(active, { fullName: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="POSITION / TITLE"
                        value={d.positionTitle}
                        placeholder="e.g. Director, CFO"
                        onChange={(e: any) =>
                            updateDirector(active, { positionTitle: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="DATE OF BIRTH"
                        type="date"
                        value={d.dateOfBirth}
                        onChange={(e: any) =>
                            updateDirector(active, { dateOfBirth: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="NATIONALITY"
                        value={d.nationality}
                        placeholder="Enter nationality"
                        onChange={(e: any) =>
                            updateDirector(active, { nationality: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="PASSPORT NO."
                        value={d.passportNo}
                        placeholder="Enter passport number"
                        onChange={(e: any) =>
                            updateDirector(active, { passportNo: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="EMAIL ID"
                        type="email"
                        value={d.email}
                        placeholder="name@example.com"
                        onChange={(e: any) =>
                            updateDirector(active, { email: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />

                    <GlobalTextInputField
                        title="PHONE NUMBER"
                        value={d.phone}
                        placeholder="+91 00000 00000"
                        onChange={(e: any) =>
                            updateDirector(active, { phone: e.target.value })
                        }
                        showBorder
                        disabled={readOnly}
                    />
                    {/* PEP */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>IS POLITICALLY EXPOSED (PEP)</Label>
                        <div className="relative">
                            <select
                                className="appearance-none p-3 pr-10 rounded-md border border-border-subtle w-full"
                                value={d.isPep}
                                onChange={(e) =>
                                    updateDirector(active, { isPep: e.target.value as any })
                                }
                                disabled={readOnly}
                            >
                                <option value="">Select</option>
                                <option value="yes">Yes</option>
                                <option value="no">No</option>
                            </select>

                            <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                                ▾
                            </span>
                        </div>
                    </div>

                    <div className="col-span-2">
                        <GlobalTextInputField
                            title="ADDRESS"
                            value={d.address}
                            placeholder="Enter address"
                            onChange={(e: any) =>
                                updateDirector(active, { address: e.target.value })
                            }
                            showBorder
                            disabled={readOnly}
                        />
                    </div>

                    {/* PEP */}





                    {/* DOCUMENTS */}
                    <div className="col-span-2 grid grid-cols-2 gap-4">
                        {/* ADDRESS PROOF */}
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>ADDRESS PROOF</Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Utility bill or lease deed dated in the last 3 months
                                </span>
                            </div>
                            <UploadTableSingle
                                value={d.addressProofUrl}
                                onChange={(url) =>
                                    updateDirector(active, { addressProofUrl: url })
                                }
                                readOnly={readOnly}
                            />
                        </div>

                        {/* PASSPORT COPY */}
                        <div className="flex flex-col gap-1 text-[10px]">
                            <Label>PASSPORT COPY</Label>
                            <div className="flex items-center gap-1">
                                <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                    <img src={Info} alt="" className="w-3 h-3" />
                                </div>
                                <span className="text-[8px]">
                                    Upload Passport copy
                                </span>
                            </div>
                            <UploadTableSingle
                                value={d.passportCopyUrl}
                                onChange={(url) =>
                                    updateDirector(active, { passportCopyUrl: url })
                                }
                                readOnly={readOnly}
                            />
                        </div>
                    </div>
                    {/* ADDITIONAL DOCUMENTS */}
                    <div className="flex flex-col gap-1 text-[10px]">
                        <Label>ADDITIONAL DOCUMENTS</Label>
                        <div className="flex items-center gap-1">
                            <div className="bg-bg-button-secondary p-[1px] rounded-full">
                                <img src={Info} alt="" className="w-3 h-3" />
                            </div>
                            <span className="text-[8px]">
                                Upload any additional documents relevant to your application
                            </span>
                        </div>
                        <UploadTable
                            values={d.additionalUrls ?? []}
                            onChange={(i, url) =>
                                updateDirector(active, {
                                    additionalUrls: d.additionalUrls.map((u, idx) =>
                                        idx === i ? url : u
                                    ),
                                })
                            }
                            add={(url) =>
                                updateDirector(active, {
                                    additionalUrls: [...d.additionalUrls, url],
                                })
                            }
                            remove={(i) =>
                                updateDirector(active, {
                                    additionalUrls: d.additionalUrls.filter((_, idx) => idx !== i),
                                })
                            }
                            readOnly={readOnly}
                        />
                    </div>

                </div>
            </div>
        </div>
    );
};

export default ApplicantEntityDirectorsStep;
